<template>
  <div>
    <div class="d-flex align-center">
      <h2 class="text-h5 font-weight-bold">帳票設定</h2>
      <v-spacer />
      <v-btn
        @click="openEditDialog()"
        :disabled="loading"
        color="primary"
        prepend-icon="mdi-plus"
        elevation="0"
      >
        新規作成
      </v-btn>
    </div>

    <!-- table -->
    <div class="mt-6">
      <v-data-table
        :headers="headers"
        :items="formResponse ? formResponse.data : []"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
        class="elevation-1"
      >
        <!-- type -->
        <template v-slot:[`item.type`]="{ item }">
          {{ getPlanText(item.type) }}
        </template>

        <!-- createdAt -->
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- edit -->
        <template v-slot:[`item.edit`]="{ item }">
          <v-btn
            @click="openEditDialog(item)"
            :disabled="loading"
            color="primary"
            variant="outlined"
            size="small"
            density="compact"
          >
            編集
          </v-btn>
        </template>

        <!-- delete -->
        <template v-slot:[`item.delete`]="{ item }">
          <v-btn
            @click="deleteForm(item.id)"
            :disabled="loading"
            color="error"
            variant="flat"
            size="small"
            density="compact"
          >
            削除
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title class="pa-4">
          <span class="text-h6">帳票項目</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6" class="text-subtitle-2"> テンプレート名称* </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.name"
                  variant="outlined"
                  density="compact"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row align="center">
              <v-col cols="6" class="text-subtitle-2"> 種類* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.type"
                  :items="planItems"
                  item-title="text"
                  item-value="value"
                  placeholder="選択してください"
                  variant="outlined"
                  density="compact"
                  hide-details
                ></v-select>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" class="font-weight-bold"> 帳票項目 </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-table density="compact" class="border">
                  <thead>
                    <tr>
                      <th class="text-left" style="width: 80px">順番</th>
                      <th class="text-left">項目</th>
                      <th class="text-center" style="width: 100px">削除</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in input.itemMastList" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td class="py-2">
                        <v-select
                          v-model="item.itemMastId"
                          :items="planFormItems"
                          item-title="text"
                          item-value="value"
                          variant="outlined"
                          density="compact"
                          hide-details
                        ></v-select>
                      </td>
                      <td class="text-center">
                        <v-btn
                          @click="deleteItemInItemList(i)"
                          color="error"
                          variant="outlined"
                        >
                          削除
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-btn
                  @click="AddItemToItemList"
                  :disabled="loading"
                  color="primary"
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-plus"
                >
                  項目追加
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn @click="editDialog = false" variant="text" color="grey-darken-1"> キャンセル </v-btn>
          <v-spacer />
          <v-btn
            v-if="isUpdate"
            @click="updateForm"
            :disabled="loading"
            color="primary"
            variant="flat"
            min-width="100"
          >
            保存
          </v-btn>
          <v-btn
            v-else
            @click="createForm"
            :disabled="loading"
            color="primary"
            variant="flat"
            min-width="100"
          >
            作成
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { GuidancePlanFormItemCategoryItems, GuidancePlanTypeItems } from "@/constants/guidance";
import {
  GuidancePlanCategoryType,
  OperationGuidancePlanFormItemMastListResponse,
  OperationGuidancePlanFormMastDetail,
  OperationGuidancePlanFormMastListResponse,
} from "chaild-api/lib";
import dayjs from "dayjs";
import { guidanceModule } from "@/store/dataModule/guidanceModule";
import LocalDataService from "@/service/LocalDataService";
import ApiGuidance from "@/api/ApiGuidance";

type ItemList = {
  itemMastId: number | null; // number単体から null を許容に変更
  order: number;
};

export default defineComponent({
  name: "GuidancePlanForm",

  async mounted() {
    await this.listForm();
    await this.listFormItem();
  },

  data() {
    return {
      loading: false,
      editDialog: false,
      planItems: GuidancePlanTypeItems,
      categoryItems: GuidancePlanFormItemCategoryItems,
      headers: [
        { title: "テンプレート名称", key: "name" }, // Vuetify 3では value ではなく key
        { title: "種類", key: "type" },
        { title: "作成日", key: "createdAt" },
        { title: "編集", key: "edit", sortable: false },
        { title: "削除", key: "delete", sortable: false },
      ] as any,
      searchFilters: {
        limit: 2000,
        skip: 0,
        type: "" as GuidancePlanCategoryType,
      },
      input: {
        name: "",
        type: "" as GuidancePlanCategoryType,
        itemMastList: [] as ItemList[],
        formMastId: 0,
      },
    };
  },

  computed: {
    isUpdate(): boolean {
      return !!this.input.formMastId;
    },
    formResponse(): OperationGuidancePlanFormMastListResponse | null {
      return guidanceModule.mastList;
    },
    itemResponse(): OperationGuidancePlanFormItemMastListResponse | null {
      return guidanceModule.itemMastList;
    },
    planFormItems(): any[] {
      if (this.itemResponse) {
        return this.itemResponse.data.map((i) => ({
          text: i.name,
          value: i.id,
        }));
      }
      return [];
    },
  },

  methods: {
    async listForm() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        await guidanceModule
          .listMast({
            companyId,
            ...this.searchFilters,
          })
          .catch(() => (this.loading = false));
      }
      this.loading = false;
    },
    async createForm() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        // 1. 順番を計算しつつ、itemMastId が null のものを除外する
        const sanitizedItems = this.input.itemMastList
          .filter((item) => item.itemMastId !== null) // null を除外
          .map((item, index) => ({
            itemMastId: item.itemMastId as number, // ここで number 型であるとアサーション
            order: index + 1,
          }));

        // 2. APIに渡すデータを作成
        const requestBody = {
          name: this.input.name,
          type: this.input.type,
          itemMastList: sanitizedItems,
        };

        const response = await ApiGuidance.createPlanFormMast({
          companyId,
          body: requestBody, // 修正した body を渡す
        }).catch(() => (this.loading = false));

        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listForm();
        }
     }
      this.loading = false;
    },
    async updateForm() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        // 同様にクレンジング処理
        const sanitizedItems = this.input.itemMastList
          .filter((item) => item.itemMastId !== null)
          .map((item, index) => ({
            itemMastId: item.itemMastId as number,
            order: index + 1,
          }));

        const response = await ApiGuidance.updatePlanFormMast({
          companyId,
          formMastId: this.input.formMastId,
          body: {
            name: this.input.name,
            type: this.input.type,
            itemMastList: sanitizedItems,
          },
        }).catch(() => (this.loading = false));

        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listForm();
        }
      }
      this.loading = false;
    },
    async deleteForm(formMastId: number) {
      if (confirm("削除しますか")) {
        this.loading = true;
        const companyId = LocalDataService.getCompanyId();
        if (companyId) {
          const response = await ApiGuidance.deletePlanFormMast({
            companyId,
            formMastId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert("削除しました");
            await this.listForm();
          }
        }
      }
      this.loading = false;
    },
    async listFormItem() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        await guidanceModule
          .listItemMast({
            companyId,
            ...this.searchFilters,
          })
          .catch(() => (this.loading = false));
      }
      this.loading = false;
    },
    openEditDialog(value?: OperationGuidancePlanFormMastDetail) {
      if (value) {
        this.setInput(value);
      } else {
        this.clearInput();
      }
      this.editDialog = true;
    },
    clearInput() {
      this.input = {
        name: "",
        type: "" as GuidancePlanCategoryType,
        itemMastList: [],
        formMastId: 0,
      };
    },
    setInput(value: OperationGuidancePlanFormMastDetail) {
      this.input = {
        name: value.name,
        formMastId: value.id,
        type: value.type,
        itemMastList: value.itemMastList.map((item) => ({
          itemMastId: item.itemMast.id,
          order: item.order,
        })),
      };
    },
    AddItemToItemList() {
      this.input.itemMastList.push({
        itemMastId: null, // 0 ではなく null に設定
        order: 999,
      });
    },
    deleteItemInItemList(index: number) {
      this.input.itemMastList.splice(index, 1);
    },
    calcItemListOrder(): ItemList[] {
      return this.input.itemMastList.map((item, index) => ({
        itemMastId: item.itemMastId,
        order: index + 1,
      }));
    },
    getPlanText(value: string): string {
      const item = this.planItems.find((p) => p.value == value);
      return item?.text || "";
    },
    formatDate(value: string): string {
      return value ? dayjs(value).format("YYYY/MM/DD") : "";
    },
  },
});
</script>